/**
 * CLASSIC LOOK (v1.70): the pre-Floodlight design from commit ea44aec, kept
 * for people/sites on the Classic UI style. Functionality must match the
 * Floodlight version (screens/WhyScreen.jsx); port behaviour changes here too.
 *
 * Why BetOptimized (/why) — the marketing page. Same visual language as
 * Plans and the Optimizer: dark ground, ClimateCrisis display type, the
 * indigo → green edge line, and the shared motion primitives (kinetic
 * headline, scroll reveals, pointer spotlight). Every motion is off under
 * reduced motion.
 *
 * Copy rule: real features only. No invented stats, results or testimonials,
 * and no claims about live EV results (odds are on a sandbox feed for now).
 */
import React from 'react';
import { View, Text, ScrollView, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import NavigationHeader from '../../components/NavigationHeader';
import { ScreenBackground } from '../../components/ThemeGradients';
import { useNavigation } from '../../context/NavigationContext';
import { useTicker } from '../../context/TickerContext';
import { useAuth } from '../../context/AuthContext';
import Appear from '../../components/motion/Appear';
import Reveal from '../../components/motion/Reveal';
import Spotlight from '../../components/motion/Spotlight';
import PressableScale from '../../components/motion/PressableScale';
import EdgeLine from '../../components/motion/EdgeLine';
import KineticHeadline from '../../components/motion/KineticHeadline';
import AmbientGlow from '../../components/motion/AmbientGlow';
import { IS_WEB } from '../../utils/motion';
import injectCss from '../../utils/injectCss';

const C = {
    ink: 'rgba(255,255,255,0.94)',
    soft: 'rgba(255,255,255,0.68)',
    faint: 'rgba(255,255,255,0.42)',
    line: 'rgba(255,255,255,0.09)',
    green: '#34D399',
    indigo: '#6366F1',
};
const DISPLAY = 'ClimateCrisis';
const UI = Platform.OS === 'web' ? 'Slate, -apple-system, system-ui, "Segoe UI", sans-serif' : undefined;

// Recognizable names from utils/sportsbooks.json (the books we price).
const BOOKS = ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'Fanatics', 'BetRivers', 'Hard Rock Bet', 'Pinnacle', 'Circa Sports', 'Bovada', 'BetOnline', 'Novig', 'Kalshi', 'Polymarket', 'PrizePicks', 'Underdog Fantasy', 'Fliff', 'Betfair'];

const STEPS = [
    ['chatbubble-ellipses-outline', 'Ask it plainly', 'Type the bet the way you think it: "lebron over 25.5 points", "chiefs -3.5", "nba player points". No menus to dig through.'],
    ['git-compare-outline', 'See every book', 'Every price for that line, side by side, devigged against the whole market so you see the fair odds, not one book\'s opinion.'],
    ['trending-up-outline', 'Take the best number', 'Ranked by expected value, with the book that pays the most and how many books carry it. Then bet it where you already play.'],
];

const FEATURES = [
    ['search-outline', 'Natural-language search', 'Players, teams, markets and lines in one box, across sports and leagues.'],
    ['layers-outline', 'Same-game parlay optimizer', 'Stack legs from one game and price the combination at the major books.'],
    ['analytics-outline', 'Fair odds on every price', 'Devigged against the full market, not a single sportsbook.'],
    ['options-outline', 'Strictness you control', 'Exact matches only, or broaden to related markets and nearby lines with one slider.'],
    ['time-outline', 'Background searches', 'Queue searches and keep browsing; results land when they are ready.'],
    ['people-outline', 'Community', 'Share bets, compare reads and talk lines with other members.'],
];

// Infinite marquee of book names (web CSS). Paused under reduced motion and on hover.
const MARQUEE_CSS = `
[data-why-marquee] { display: flex; width: max-content; animation: boWhyMarquee 48s linear infinite; }
[data-why-marquee-wrap]:hover [data-why-marquee] { animation-play-state: paused; }
[data-why-marquee-wrap] { -webkit-mask-image: linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent); mask-image: linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent); }
@keyframes boWhyMarquee { to { transform: translate3d(-50%, 0, 0); } }
@media (prefers-reduced-motion: reduce) { [data-why-marquee] { animation: none; flex-wrap: wrap; width: auto; justify-content: center; } [data-why-dup] { display: none; } }
`;

export default function WhyScreen() {
    const { navigateTo } = useNavigation();
    const { user } = useAuth();
    const { totalHeaderHeight } = useTicker();
    const { width } = useWindowDimensions();
    const padX = width >= 1024 ? 48 : width >= 600 ? 28 : 16;
    const wide = width >= 900;
    if (IS_WEB) injectCss('why-marquee', MARQUEE_CSS);

    const primary = user
        ? { label: 'Open the Optimizer', to: 'SameGameParlay' }
        : { label: 'Start free', to: 'Register' };
    // Quick routes (same set as the Floodlight /why "Go straight to" list).
    const jumps = [
        ['Search any line', 'Home'],
        ['Open the Optimizer', 'SameGameParlay'],
        ['Compare plans', 'Plans'],
        ['Join the community', 'Community'],
    ];

    return (
        <View style={styles.root}>
            <ScreenBackground />
            <NavigationHeader />
            <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={{ paddingTop: (Platform.OS === 'web' ? totalHeaderHeight : 0) + 24, paddingBottom: 24 }}
                showsVerticalScrollIndicator={false}
            >
                {/* Hero */}
                <View style={[styles.hero, { paddingHorizontal: padX, minHeight: wide ? 560 : undefined }]}>
                    <AmbientGlow />
                    <EdgeLine height={wide ? 200 : 130} style={styles.heroLine} opacity={0.6} />
                    <Appear>
                        <Text style={styles.eyebrow}>BETOPTIMIZED</Text>
                    </Appear>
                    <KineticHeadline
                        lines={['Every book.', 'Every market.', 'One edge.']}
                        textStyle={[styles.display, { fontSize: width >= 1200 ? 92 : width >= 768 ? 64 : width >= 420 ? 44 : 29, lineHeight: width >= 1200 ? 94 : width >= 768 ? 68 : width >= 420 ? 48 : 33 }]}
                    />
                    <Appear index={3}>
                        <Text style={[styles.lede, { maxWidth: 640 }]}>
                            BetOptimized prices every bet against the whole market. Search any line in plain English, see every sportsbook side by side with the fair odds, and take the best number.
                        </Text>
                    </Appear>
                    <Appear index={4} style={styles.ctaRow}>
                        <PressableScale onPress={() => navigateTo(primary.to)} style={[styles.cta, styles.ctaPrimary]} hoverStyle={styles.ctaPrimaryHover} accessibilityRole="button">
                            <Text style={[styles.ctaText, { color: '#0B0B12' }]}>{primary.label}</Text>
                            <Ionicons name="arrow-forward" size={16} color="#0B0B12" />
                        </PressableScale>
                        <PressableScale onPress={() => navigateTo('Plans')} style={[styles.cta, styles.ctaGhost]} hoverStyle={styles.ctaGhostHover} accessibilityRole="button">
                            <Text style={styles.ctaText}>See plans</Text>
                        </PressableScale>
                    </Appear>
                    <Appear index={5} style={styles.jumps}>
                        <Text style={styles.jumpsLabel}>GO STRAIGHT TO</Text>
                        <View style={styles.jumpRow}>
                            {jumps.map(([t, to]) => (
                                <PressableScale key={to} onPress={() => navigateTo(to)} style={styles.jump} hoverStyle={styles.jumpHover} accessibilityRole="link" accessibilityLabel={t}>
                                    <Text style={styles.jumpText}>{t}</Text>
                                    <Ionicons name="arrow-forward" size={14} color={C.green} />
                                </PressableScale>
                            ))}
                        </View>
                    </Appear>
                </View>

                {/* Books marquee */}
                <Reveal style={{ marginTop: wide ? 8 : 32 }}>
                    <Text style={styles.kicker}>PRICED SIDE BY SIDE</Text>
                    <View style={styles.marqueeWrap} {...(IS_WEB ? { dataSet: { whyMarqueeWrap: '' } } : {})} accessibilityLabel={`Sportsbooks including ${BOOKS.join(', ')}`}>
                        <View style={styles.marqueeRow} {...(IS_WEB ? { dataSet: { whyMarquee: '' } } : {})}>
                            {(IS_WEB ? [...BOOKS, ...BOOKS] : BOOKS).map((b, i) => (
                                <Text key={`${b}-${i}`} style={styles.book} aria-hidden={i >= BOOKS.length} {...(i >= BOOKS.length ? { dataSet: { whyDup: '' } } : {})}>{b}</Text>
                            ))}
                        </View>
                    </View>
                    <Text style={styles.marqueeNote}>Major US books, sharp offshore books, exchanges and prediction markets, and DFS apps.</Text>
                </Reveal>

                {/* How it works */}
                <View style={[styles.section, { paddingHorizontal: padX }]}>
                    <Reveal>
                        <Text style={styles.kicker}>HOW IT WORKS</Text>
                        <Text style={[styles.sectionTitle, { fontSize: wide ? 44 : 30, lineHeight: wide ? 48 : 34 }]} accessibilityRole="header" aria-level={2}>Three steps to the number</Text>
                    </Reveal>
                    <View style={[styles.steps, !wide && { flexDirection: 'column' }]}>
                        {STEPS.map(([icon, title, body], i) => (
                            <Reveal key={title} index={i} style={{ flex: 1 }}>
                                <Spotlight style={styles.step} lift={false} glow="rgba(99,102,241,0.14)">
                                    <View style={styles.stepHead}>
                                        <Text style={styles.stepNum} aria-hidden>{String(i + 1).padStart(2, '0')}</Text>
                                        <Ionicons name={icon} size={22} color={C.green} accessibilityElementsHidden />
                                    </View>
                                    <Text style={styles.stepTitle} accessibilityRole="header" aria-level={3}>{title}</Text>
                                    <Text style={styles.stepBody}>{body}</Text>
                                </Spotlight>
                            </Reveal>
                        ))}
                    </View>
                </View>

                {/* Statement */}
                <Reveal style={[styles.section, { paddingHorizontal: padX, alignItems: 'center' }]}>
                    <EdgeLine variant="flat" height={44} opacity={0.7} />
                    <Text style={[styles.statement, { fontSize: wide ? 40 : 26, lineHeight: wide ? 48 : 32 }]}>
                        The market already knows the fair price.{' '}
                        <Text style={{ color: C.green }}>We show you where a book disagrees.</Text>
                    </Text>
                </Reveal>

                {/* Features */}
                <View style={[styles.section, { paddingHorizontal: padX }]}>
                    <Reveal>
                        <Text style={styles.kicker}>WHAT YOU GET</Text>
                        <Text style={[styles.sectionTitle, { fontSize: wide ? 44 : 30, lineHeight: wide ? 48 : 34 }]} accessibilityRole="header" aria-level={2}>Built for the number</Text>
                    </Reveal>
                    <View style={styles.grid} accessibilityRole="list">
                        {FEATURES.map(([icon, title, body], i) => (
                            <View key={title} style={{ width: width >= 1100 ? '33.333%' : width >= 700 ? '50%' : '100%', padding: 8 }} accessibilityRole="listitem">
                                <Reveal index={i} style={{ flex: 1 }}>
                                    <Spotlight style={styles.feature}>
                                        <View style={styles.featureIcon}><Ionicons name={icon} size={20} color={C.green} accessibilityElementsHidden /></View>
                                        <Text style={styles.featureTitle}>{title}</Text>
                                        <Text style={styles.featureBody}>{body}</Text>
                                    </Spotlight>
                                </Reveal>
                            </View>
                        ))}
                    </View>
                </View>

                {/* Closing call to action */}
                <Reveal style={[styles.section, { paddingHorizontal: padX }]}>
                    <Spotlight style={styles.closer} lift={false}>
                        <EdgeLine height={wide ? 130 : 90} style={styles.closerLine} opacity={0.5} delay={150} />
                        <Text style={[styles.display, { fontSize: wide ? 56 : 32, lineHeight: wide ? 60 : 36, textAlign: 'center' }]} accessibilityRole="header" aria-level={2}>Find your edge.</Text>
                        <Text style={[styles.lede, { marginTop: 4, maxWidth: 520 }]}>{user ? 'Your account is ready. Run your first search in the Optimizer.' : 'Start on the free plan. No card needed, upgrade when you want more searches.'}</Text>
                        <View style={styles.ctaRow}>
                            <PressableScale onPress={() => navigateTo(primary.to)} style={[styles.cta, styles.ctaPrimary]} hoverStyle={styles.ctaPrimaryHover} accessibilityRole="button">
                                <Text style={[styles.ctaText, { color: '#0B0B12' }]}>{primary.label}</Text>
                                <Ionicons name="arrow-forward" size={16} color="#0B0B12" />
                            </PressableScale>
                            <PressableScale onPress={() => navigateTo('Plans')} style={[styles.cta, styles.ctaGhost]} hoverStyle={styles.ctaGhostHover} accessibilityRole="button">
                                <Text style={styles.ctaText}>Compare plans</Text>
                            </PressableScale>
                        </View>
                    </Spotlight>
                </Reveal>

                <View style={{ height: 48 }} />
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create({
    root: { flex: 1, backgroundColor: '#000', ...(Platform.OS === 'web' ? { minHeight: '100vh' } : {}) },
    hero: { alignItems: 'center', justifyContent: 'center', paddingTop: 40, paddingBottom: 40, position: 'relative' },
    heroLine: { position: 'absolute', left: 0, right: 0, top: 40 },
    eyebrow: { color: C.green, fontFamily: UI, fontSize: 12, fontWeight: '700', letterSpacing: 5, marginBottom: 18 },
    display: { color: C.ink, fontFamily: DISPLAY, textTransform: 'uppercase', letterSpacing: -1 },
    lede: { color: C.soft, fontFamily: UI, fontSize: 18, lineHeight: 28, textAlign: 'center', marginTop: 24 },
    ctaRow: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 12, marginTop: 28 },
    cta: { minHeight: 50, borderRadius: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingHorizontal: 24 },
    ctaPrimary: { backgroundColor: C.green },
    ctaPrimaryHover: { backgroundColor: '#4ADEAA', ...(Platform.OS === 'web' ? { boxShadow: '0 12px 36px -10px rgba(52,211,153,0.6)' } : {}) },
    ctaGhost: { backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.16)' },
    ctaGhostHover: { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.28)' },
    ctaText: { color: C.ink, fontFamily: UI, fontSize: 16, fontWeight: '800' },

    jumps: { alignItems: 'center', marginTop: 32, gap: 12 },
    jumpsLabel: { color: C.faint, fontFamily: UI, fontSize: 11, fontWeight: '700', letterSpacing: 4 },
    jumpRow: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 8 },
    jump: { minHeight: 40, flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, borderRadius: 999, borderWidth: 1, borderColor: C.line, backgroundColor: 'rgba(255,255,255,0.03)' },
    jumpHover: { borderColor: 'rgba(52,211,153,0.45)', backgroundColor: 'rgba(52,211,153,0.08)' },
    jumpText: { color: C.soft, fontFamily: UI, fontSize: 14, fontWeight: '700' },

    kicker: { color: C.green, fontFamily: UI, fontSize: 12, fontWeight: '700', letterSpacing: 4, textAlign: 'center' },
    marqueeWrap: { overflow: 'hidden', marginTop: 18, paddingVertical: 6 },
    marqueeRow: { flexDirection: 'row', flexWrap: Platform.OS === 'web' ? 'nowrap' : 'wrap', justifyContent: 'center' },
    book: { color: 'rgba(255,255,255,0.55)', fontFamily: DISPLAY, fontSize: 22, textTransform: 'uppercase', paddingHorizontal: 22, paddingVertical: 6 },
    marqueeNote: { color: C.faint, fontFamily: UI, fontSize: 13, textAlign: 'center', marginTop: 12, paddingHorizontal: 16 },

    section: { marginTop: 96, alignSelf: 'center', width: '100%', maxWidth: 1240 },
    sectionTitle: { color: C.ink, fontFamily: DISPLAY, textTransform: 'uppercase', textAlign: 'center', marginTop: 12, letterSpacing: -0.5 },
    steps: { flexDirection: 'row', gap: 16, marginTop: 36 },
    step: { flex: 1, gap: 10, padding: 26, borderRadius: 20, borderWidth: 1, borderColor: C.line, backgroundColor: 'rgba(255,255,255,0.02)', overflow: 'hidden' },
    stepHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
    stepNum: { color: 'rgba(255,255,255,0.16)', fontFamily: DISPLAY, fontSize: 44, letterSpacing: -1 },
    stepTitle: { color: C.ink, fontFamily: UI, fontSize: 19, fontWeight: '800' },
    stepBody: { color: C.soft, fontFamily: UI, fontSize: 15, lineHeight: 23 },

    statement: { color: C.ink, fontFamily: UI, fontWeight: '800', textAlign: 'center', maxWidth: 900, marginTop: 18, letterSpacing: -0.5 },

    grid: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 28, marginHorizontal: -8 },
    feature: { flex: 1, gap: 8, padding: 24, borderRadius: 18, borderWidth: 1, borderColor: C.line, backgroundColor: 'rgba(22,22,34,0.85)', overflow: 'hidden' },
    featureIcon: { width: 40, height: 40, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(52,211,153,0.08)', borderWidth: 1, borderColor: 'rgba(52,211,153,0.25)', marginBottom: 8 },
    featureTitle: { color: C.ink, fontFamily: UI, fontSize: 17, fontWeight: '800' },
    featureBody: { color: C.soft, fontFamily: UI, fontSize: 14, lineHeight: 21 },

    closer: { alignItems: 'center', gap: 12, paddingVertical: 64, paddingHorizontal: 24, borderRadius: 28, borderWidth: 1, borderColor: C.line, backgroundColor: 'rgba(14,14,24,0.85)', overflow: 'hidden' },
    closerLine: { position: 'absolute', left: 0, right: 0, top: 10 },
});
